import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { cn } from "util/cn";
import { Button } from "ui/Button";
import { Calendar } from "ui/Calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "ui/Popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/InputSelect";
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
} from "ui/Drawer";

interface DateTimePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  onClose?: () => void;
  isMobile?: boolean;
}

export function DateTimePicker({ 
  value, 
  onChange, 
  onClose,
  isMobile = false
}: DateTimePickerProps) {
  const [date, setDate] = useState<Date | null>(value);
  
  // Update internal date when prop changes
  useEffect(() => {
    setDate(value);
  }, [value]);

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 12 }, (_, i) => i * 5);
  
  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) return;
    
    // Preserve the time from the existing date if available
    const newDate = new Date(selectedDate);
    if (date) {
      newDate.setHours(date.getHours(), date.getMinutes());
    } else {
      // Default to 12:00 PM (noon) when first selecting a date
      newDate.setHours(12, 0, 0, 0);
    }
    
    setDate(newDate);
    onChange(newDate);
  };

  const handleTimeChange = (
    type: "hour" | "minute" | "ampm",
    value: string
  ) => {
    if (!date) {
      // If no date is set, use today
      const newDate = new Date();
      newDate.setHours(0, 0, 0, 0); // Start with midnight
      setDate(newDate);
      
      // Then apply the time change
      handleTimeChangeWithDate(newDate, type, value);
    } else {
      // Apply time change to existing date
      handleTimeChangeWithDate(date, type, value);
    }
  };
  
  const handleTimeChangeWithDate = (
    currentDate: Date,
    type: "hour" | "minute" | "ampm",
    value: string
  ) => {
    const newDate = new Date(currentDate);
    
    if (type === "hour") {
      // Convert to 24-hour format considering AM/PM
      const isPM = newDate.getHours() >= 12;
      const hourValue = parseInt(value);
      const hour24 = (hourValue % 12) + (isPM ? 12 : 0);
      newDate.setHours(hour24);
    } else if (type === "minute") {
      newDate.setMinutes(parseInt(value));
    } else if (type === "ampm") {
      const currentHour = newDate.getHours();
      const hour12 = currentHour % 12;
      
      if (value === "PM" && currentHour < 12) {
        // Switching to PM
        newDate.setHours(hour12 + 12);
      } else if (value === "AM" && currentHour >= 12) {
        // Switching to AM
        newDate.setHours(hour12);
      }
    }
    
    setDate(newDate);
    onChange(newDate);
  };
  
  // Format display values
  const getCurrentHour = () => {
    if (!date) return "";
    const hour = date.getHours() % 12;
    return hour === 0 ? "12" : hour.toString();
  };
  
  const getCurrentMinute = () => {
    if (!date) return "";
    // Round to nearest 5 minute increment
    const minute = date.getMinutes();
    const roundedMinute = Math.round(minute / 5) * 5;
    return roundedMinute.toString().padStart(2, "0");
  };
  
  const getCurrentAMPM = () => {
    if (!date) return "";
    return date.getHours() >= 12 ? "PM" : "AM";
  };
  
  const content = (
    <div className="w-auto">
      <div className="sm:flex">
        <Calendar
          mode="single"
          selected={date || undefined}
          onSelect={(selectedDate) => handleDateSelect(selectedDate)}
          initialFocus
        />
        <div className="flex flex-col border-t sm:border-t-0 sm:border-l border-border">
          <div className="grid grid-cols-3 p-3 gap-2">
            {/* Hours */}
            <Select 
              value={getCurrentHour()}
              onValueChange={(value) => handleTimeChange("hour", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Hour" />
              </SelectTrigger>
              <SelectContent>
                {hours.map((hour) => (
                  <SelectItem key={hour} value={hour.toString()}>
                    {hour}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Minutes */}
            <Select 
              value={getCurrentMinute()}
              onValueChange={(value) => handleTimeChange("minute", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Min" />
              </SelectTrigger>
              <SelectContent>
                {minutes.map((minute) => (
                  <SelectItem key={minute} value={minute.toString().padStart(2, "0")}>
                    {minute.toString().padStart(2, "0")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* AM/PM */}
            <Select 
              value={getCurrentAMPM()}
              onValueChange={(value) => handleTimeChange("ampm", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="AM/PM" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="AM">AM</SelectItem>
                <SelectItem value="PM">PM</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      
      {/* Done button for mobile */}
      {isMobile && onClose && (
        <div className="p-3 border-t">
          <Button 
            onClick={onClose}
            className="w-full"
          >
            Done
          </Button>
        </div>
      )}
    </div>
  );
  
  // For mobile rendering
  if (isMobile) {
    return content;
  }
  
  // For desktop rendering
  return content;
}

export function DateTimePickerInput({
  value,
  onChange,
  isMobile = false,
  className,
  placeholder = "Select date and time",
  onOpenChange: onParentOpenChange
}: {
  value: Date | null;
  onChange: (date: Date | null) => void;
  isMobile?: boolean;
  className?: string;
  placeholder?: string;
  onOpenChange?: (open: boolean) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  
  const handleChange = (date: Date | null) => {
    onChange(date);
    if (!isMobile) {
      // Close popover after selection on desktop
      // setIsOpen(false);
    }
  };
  
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    // Notify parent component about open state
    if (onParentOpenChange) {
      onParentOpenChange(open);
    }
  };
  
  const trigger = (
    <Button
      variant="outline"
      className={cn(
        "w-full justify-start text-left font-normal h-9",
        !value && "text-muted-foreground",
        className
      )}
      type="button"
    >
      <CalendarIcon className="mr-2 h-4 w-4" />
      {value ? format(value, "MM/dd/yyyy hh:mm aa") : <span>{placeholder}</span>}
    </Button>
  );
  
  // Mobile: render in drawer
  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={handleOpenChange}>
        <DrawerTrigger asChild>
          {trigger}
        </DrawerTrigger>
        <DrawerContent className="p-0">
          <DateTimePicker
            value={value}
            onChange={handleChange}
            onClose={() => setIsOpen(false)}
            isMobile={true}
          />
        </DrawerContent>
      </Drawer>
    );
  }
  
  // Desktop: render in popover
  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {trigger}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <DateTimePicker
          value={value}
          onChange={handleChange}
        />
      </PopoverContent>
    </Popover>
  );
} 